import { LogoSpinner } from "@via/components/loading/loading";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import { usePipelineBuilderOptions } from "@via/pages/projects/pipelines/pipelines.api.tsx";
import PipelineGraph from "@via/pages/projects/pipelines/workflow/pipeline-graph.tsx";
import { ReactFlowProvider } from "@xyflow/react";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import { useCallback } from "react";
import { ROUTES } from "@via/utils/constants";

export default function Workflow(): JSX.Element {
  const { pipelineDraft, parameters } = useGlobal();
  const { data: builderOptionsData } = usePipelineBuilderOptions();

  const getPipelineWorkflowContext = useCallback(() => {
    // Extract nodes and edges from pipelineDraft
    const nodes = pipelineDraft?.nodes
      ? Object.keys(pipelineDraft.nodes).map(key => {
          const node = pipelineDraft.nodes[key];
          return {
            id: key.split("-")[1] || key,
            name: node?.nodeName,
            type: node?.type,
            position: {
              x: node?.xCoordinate,
              y: node?.yCoordinate,
            },
          };
        })
      : [];

    const edges = pipelineDraft?.edges || [];

    // Extract process and pipeline names from builderOptionsData, grouped by category
    const extractNamesByGroup = (
      nodes: any[],
      nodeType: string
    ): Record<string, Array<{ id: number; name: string }>> => {
      const result: Record<string, Array<{ id: number; name: string }>> = {};
      const traverse = (nodeList: any[], parentGroup: string | null = null) => {
        for (const node of nodeList) {
          // If this is a category, use its name as the group for children
          const currentGroup =
            node.nodeType === "category" ? node.name : parentGroup;

          if (node.nodeType === nodeType) {
            const groupKey = parentGroup || "Uncategorized";
            if (!result[groupKey]) {
              result[groupKey] = [];
            }
            result[groupKey].push({
              id: node.id,
              name: node.name,
            });
          }
          if (node.children && node.children.length > 0) {
            traverse(node.children, currentGroup);
          }
        }
      };
      traverse(nodes);
      return result;
    };

    const builderOptionsNodes = builderOptionsData?.data || [];
    const processesByGroup = extractNamesByGroup(
      builderOptionsNodes,
      "processNode"
    );
    const pipelinesByGroup = extractNamesByGroup(
      builderOptionsNodes,
      "moduleNode"
    );
    const skillsByGroup = extractNamesByGroup(builderOptionsNodes, "skillNode");
    const modelsByGroup = extractNamesByGroup(builderOptionsNodes, "modelNode");

    // Limit items per group and track totals
    const limitPerGroup = 50;
    let totalProcesses = 0;
    let totalPipelines = 0;
    let totalSkills = 0;
    let totalModels = 0;
    let hasMoreProcesses = false;
    let hasMorePipelines = false;
    let hasMoreSkills = false;
    let hasMoreModels = false;
    const limitedProcessesByGroup: Record<
      string,
      Array<{ id: number; name: string }>
    > = {};
    const limitedPipelinesByGroup: Record<
      string,
      Array<{ id: number; name: string }>
    > = {};
    const limitedSkillsByGroup: Record<
      string,
      Array<{ id: number; name: string }>
    > = {};
    const limitedModelsByGroup: Record<
      string,
      Array<{ id: number; name: string }>
    > = {};

    for (const [group, items] of Object.entries(processesByGroup)) {
      totalProcesses += items.length;
      if (items.length > limitPerGroup) {
        hasMoreProcesses = true;
      }
      limitedProcessesByGroup[group] = items.slice(0, limitPerGroup);
    }

    for (const [group, items] of Object.entries(pipelinesByGroup)) {
      totalPipelines += items.length;
      if (items.length > limitPerGroup) {
        hasMorePipelines = true;
      }
      limitedPipelinesByGroup[group] = items.slice(0, limitPerGroup);
    }

    for (const [group, items] of Object.entries(skillsByGroup)) {
      totalSkills += items.length;
      if (items.length > limitPerGroup) {
        hasMoreSkills = true;
      }
      limitedSkillsByGroup[group] = items.slice(0, limitPerGroup);
    }

    for (const [group, items] of Object.entries(modelsByGroup)) {
      totalModels += items.length;
      if (items.length > limitPerGroup) {
        hasMoreModels = true;
      }
      limitedModelsByGroup[group] = items.slice(0, limitPerGroup);
    }

    return {
      pipeline: {
        id: pipelineDraft?.id,
        name: pipelineDraft?.name,
        isEditingAllowed: pipelineDraft?.isEditingAllowed,
      },
      workflow: {
        nodeCount: nodes.length,
        edgeCount: edges.length,
        nodes: nodes.slice(0, 50), // Limit to first 50 nodes
        edges: edges.slice(0, 100), // Limit to first 100 edges
        hasMoreNodes: nodes.length > 50,
        hasMoreEdges: edges.length > 100,
      },
      builderOptions: {
        processes: limitedProcessesByGroup,
        pipelines: limitedPipelinesByGroup,
        skills: limitedSkillsByGroup,
        models: limitedModelsByGroup,
        totalProcesses,
        totalPipelines,
        totalSkills,
        totalModels,
        hasMoreProcesses,
        hasMorePipelines,
        hasMoreSkills,
        hasMoreModels,
      },
    };
  }, [pipelineDraft, builderOptionsData]);

  useRegisterCortexContext({
    routePattern: ROUTES.PIPELINE_WORKFLOW,
    contextName: "Pipeline Workflow",
    getContext: getPipelineWorkflowContext,
    systemPrompt:
      "You are analyzing pipeline workflow page. Help answer questions about the pipeline workflow, nodes, edges, connections, and available processes and pipelines that can be added to the workflow.",
    enabled: !!pipelineDraft && !!builderOptionsData && !!parameters,
  });

  if (!pipelineDraft || !builderOptionsData || !parameters) {
    return (
      <div className="via-loading-spinner">
        <LogoSpinner />
      </div>
    );
  }

  return (
    <ReactFlowProvider>
      {pipelineDraft && builderOptionsData && (
        <PipelineGraph
          builderOptions={builderOptionsData}
          parameters={parameters}
        />
      )}
    </ReactFlowProvider>
  );
}
